/* Блок «Съёмка» движка шаблона (BotCam, legacy/bot-cam.js) ↔ сервер (engine-kit, план M3 §2 S3; photo P4; shoot, pshoot, video).
   BotCam строит свой индекс словарей при загрузке (IDX, bot-cam.js:249) и подменить его на месте нельзя, поэтому движок показывает
   блок только там, где его данные совпадают с сервером:
   - сервер публикует строки блока экрана (settings.shoot.rows, studio-shoot-model.ts shootPolicy);
   - словари сервера (GET /app/settings/dicts, кэш loadShootDicts) той же версии, что демо-снимок шаблона (dicts.version === BotCam.VER);
   - строки экрана сейчас (shootKinds: что в рамке и пресет видео сужают набор) — те же виды, что BotCam.rowsFor(SHOOT.state()).
   Иначе has() — false: экран прячет блок (решение Q-PV5), в заказ shoot не уходит. order() — выбор блока только по строкам экрана (inputs.shoot,
   JSON), restore() — «Изменить» и повтор из inputs.shoot. Сервер проверит выбор сам (422 shoot_invalid). */
import { loadShootDicts, shootKinds, shootPolicy, shootSelection } from '../studio-shoot-model';
import type { StudioForm } from '../studio-api';

/** Блок экрана BotCam.block(…) (bot-cam.js раздел 6) */
export interface CamBlock {
  state(): unknown;
  order(): Record<string, string> | null;
  restore(shoot: Record<string, string>): void;
}
/** То, что мост читает у window.BotCam */
export interface CamModule {
  VER: string;
  rowsFor(state: unknown): Array<{ kind: string }>;
}
export interface CamBridgeOptions {
  block(): CamBlock | null;
  form(): StudioForm | null | undefined;
  inputs(): Record<string, string>;
  cam?(): CamModule | null;
}
export interface CamBridge {
  /** Словари сервера (один раз на приложение); не пришли — блока нет */
  load(signal?: AbortSignal): Promise<void>;
  /** Версия словарей сервера (null — ещё нет или не пришли) */
  version(): string | null;
  /** Показывать ли блок «Съёмка» */
  has(): boolean;
  /** inputs.shoot (JSON) или undefined — блока нет */
  order(): string | undefined;
  /** Выбор блока из входов (по умолчанию — inputs.shoot) */
  restore(raw?: string): void;
}

const camModule = (): CamModule | null => (window as unknown as { BotCam?: CamModule }).BotCam ?? null;
const sorted = (kinds: string[]) => [...new Set(kinds)].sort().join(',');

export function createCamBridge(o: CamBridgeOptions): CamBridge {
  let version: string | null = null;
  const cam = () => (o.cam ? o.cam() : camModule());
  /** Виды строк экрана сейчас по серверу, если блок можно показать; иначе null */
  function kinds(): string[] | null {
    const form = o.form(),
      block = o.block(),
      module = cam();
    if (!form || !block || !module || !version || version !== module.VER || !shootPolicy(form)) return null;
    const server = shootKinds(form, o.inputs());
    if (!server.length) return null;
    const template = module.rowsFor(block.state()).map(row => row.kind);
    return sorted(server) === sorted(template) ? server : null;
  }
  return {
    async load(signal) {
      try {
        const dicts = await loadShootDicts(signal);
        version = typeof dicts.version === 'string' && dicts.version ? dicts.version : null;
      } catch {
        if (!signal?.aborted) version = null;
      }
    },
    version: () => version,
    has: () => kinds() !== null,
    order() {
      const rows = kinds(),
        picked = rows ? o.block()?.order() : null;
      if (!rows || !picked) return undefined;
      return JSON.stringify(Object.fromEntries(rows.filter(kind => typeof picked[kind] === 'string').map(kind => [kind, picked[kind]])));
    },
    restore(raw = o.inputs().shoot) {
      if (!kinds()) return;
      o.block()?.restore(shootSelection(raw));
    },
  };
}
